import { Injectable, computed, inject, signal } from '@angular/core';
import { forkJoin, map, Observable, switchMap, tap } from 'rxjs';
import { ApiService } from '../api.service';
import { Package } from '../models';

@Injectable({ providedIn: 'root' })
export class PackageDomainService {
  private readonly api = inject(ApiService);

  private readonly clientPackagesSignal = signal<Package[] | null>(null);
  readonly clientPackages = computed(() => this.clientPackagesSignal());

  private readonly projectPackagesSignal = signal<Package[] | null>(null);
  readonly projectPackages = computed(() => this.projectPackagesSignal());

  private readonly participantPackagesSignal = signal<Package[] | null>(null);
  readonly participantPackages = computed(() => this.participantPackagesSignal());

  loadClientPackages(): Observable<Package[]> {
    return this.api
      .getClientPackages()
      .pipe(tap((packages) => this.clientPackagesSignal.set(packages)));
  }

  loadProjectPackages(projectId: string): Observable<Package[]> {
    return this.api
      .getProjectPackages(projectId)
      .pipe(tap((packages) => this.projectPackagesSignal.set(packages)));
  }

  loadParticipantPackages(participantId: string): Observable<Package[]> {
    return this.api
      .getParticipantPackages(participantId)
      .pipe(tap((packages) => this.participantPackagesSignal.set(packages)));
  }

  addProjectPackage(projectId: string, pkg: Package): Observable<void> {
    return this.api.addProjectPackage(projectId, pkg).pipe(
      tap(() => {
        const current = this.projectPackagesSignal();
        if (current) {
          const filtered = current.filter((p) => p.id !== pkg.id);
          this.projectPackagesSignal.set([...filtered, pkg]);
        } else {
          this.projectPackagesSignal.set([pkg]);
        }
      }),
    );
  }

  addParticipantPackage(participantId: string, pkg: Package): Observable<void> {
    return this.api.addParticipantPackage(participantId, pkg).pipe(
      tap(() => {
        const current = this.participantPackagesSignal();
        if (current) {
          const filtered = current.filter((p) => p.id !== pkg.id);
          this.participantPackagesSignal.set([...filtered, pkg]);
        } else {
          this.participantPackagesSignal.set([pkg]);
        }
      }),
    );
  }

  deleteProjectPackage(projectId: string, packageId: string): Observable<void> {
    return this.api.deleteProjectPackage(projectId, packageId).pipe(
      tap(() => {
        const current = this.projectPackagesSignal();
        if (current) {
          this.projectPackagesSignal.set(current.filter((p) => p.id !== packageId));
        }
      }),
    );
  }

  deleteParticipantPackage(participantId: string, packageId: string): Observable<void> {
    return this.api.deleteParticipantPackage(participantId, packageId).pipe(
      tap(() => {
        const current = this.participantPackagesSignal();
        if (current) {
          this.participantPackagesSignal.set(current.filter((p) => p.id !== packageId));
        }
      }),
    );
  }

  updateProjectPackage(projectId: string, pkg: Package, updateGenerated = false): Observable<void> {
    return forkJoin([
      this.api.updateProjectPackage(projectId, pkg),
      this.api.updateProjectParticipantPackages(projectId, pkg, updateGenerated),
    ]).pipe(
      tap(() => {
        const currentProjectPackages = this.projectPackagesSignal();
        if (currentProjectPackages) {
          this.projectPackagesSignal.set(
            currentProjectPackages.map((p) => (p.id === pkg.id ? pkg : p)),
          );
        }

        const currentParticipantPackages = this.participantPackagesSignal();
        if (currentParticipantPackages) {
          this.participantPackagesSignal.set(
            currentParticipantPackages.map((p) => (p.id === pkg.id ? pkg : p)),
          );
        }
      }),
      map(() => void 0),
    );
  }

  updateParticipantPackageAndGenerate(
    clientId: string,
    projectId: string,
    participantId: string,
    pkg: Package,
  ): Observable<void> {
    return this.api.updateParticipantPackage(participantId, pkg).pipe(
      switchMap(() =>
        this.api.generateParticipantReport(clientId, projectId, participantId, pkg.id),
      ),
      tap(() => {
        const currentParticipantPackages = this.participantPackagesSignal();
        if (currentParticipantPackages) {
          this.participantPackagesSignal.set(
            currentParticipantPackages.map((p) => (p.id === pkg.id ? pkg : p)),
          );
        }
      }),
      map(() => void 0),
    );
  }

  clearCache(): void {
    this.clientPackagesSignal.set(null);
    this.projectPackagesSignal.set(null);
    this.participantPackagesSignal.set(null);
  }
}
